<template>
  <v-menu
    v-model="menuOpen"
    :open-on-click="false"
    close-on-content-click
    :activator="props.activator"
    location="bottom"
    transition="slide-y-transition"
    content-class="AppBarDocumentSearch__results elevation-6 rounded-lg"
  >
    <template #default>
      <div
        class="AppBarDocumentSearch__results__container"
        data-locator="document-search-results"
      >
        <template v-if="isPending">
          <div class="px-4 pt-4 text-grey-700 font-weight-medium text--small">
            {{ $t('common.app-bar.AppBarDocumentSearch.searchResults') }}
          </div>

          <AppBarDocumentSearchResultsLoader
            data-locator="document-search-loading"
          />
        </template>

        <template v-else>
          <template v-if="searchResults.length">
            <div class="px-4 pt-4 text-grey-700 font-weight-medium text--small">
              <span v-if="!query">
                {{ $t('common.app-bar.AppBarDocumentSearch.recentFiles') }}
              </span>
              <span v-else>
                {{ $t('common.app-bar.AppBarDocumentSearch.searchResults') }}
                <template v-if="searchResultsTotalCount">
                  ({{ searchResultsTotalCount }})
                </template>
              </span>
            </div>

            <div class="AppBarDocumentSearch__scrollable-list">
              <v-list density="compact">
                <AppBarDocumentSearchResultsItem
                  v-for="result in searchResults"
                  class="mt-2"
                  :key="result.id"
                  :file="{ ...result.source, highlight: result.highlight }"
                  @click:result="menuOpen = false"
                />
              </v-list>
            </div>

            <v-divider class="mt-2" style="border-color: #d1d1d1;" />

            <router-link
              v-if="query"
              :to="{ name: searchPageName, query: { query }, params: searchPageParams }"
              class="d-flex align-center justify-space-between cursor-pointer ma-4"
              data-locator="document-search-show-all"
              style="text-decoration: none;"
            >
              <div class="d-flex align-center">
                <app-icon-ds class="mr-2" icon="search" size="xxs" />
                <span class="text-grey-900 text--small">
                  {{ $t('common.app-bar.AppBarDocumentSearch.showAllResults') }}
                </span>
              </div>
              <v-chip v-if="mobile" color="grey-300" label density="compact">
                <app-icon-ds icon="arrow-turn-down-left" size="xxs" />
              </v-chip>
              <span v-else class="text-caption text-grey-700">
                {{ $t('common.app-bar.AppBarDocumentSearch.pressEnter') }}
              </span>
            </router-link>
          </template>
          <DocumentSearchEmptyState v-else-if="hasSearched" />
        </template>
      </div>
    </template>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'

import { getRoomRouteTypeFromType } from '@/common/utils/files'
import { GLOBAL_SEARCH_ROUTE_NAME, PROJECT_SEARCH_ROUTE_NAME } from '@/router'

import AppBarDocumentSearchResultsItem from './AppBarDocumentSearchResultsItem.vue'
import AppBarDocumentSearchResultsLoader from './AppBarDocumentSearchResultsLoader.vue'
import DocumentSearchEmptyState from '../document-search/DocumentSearchEmptyState.vue'

const props = defineProps<{
  query: string | null
  activator?: HTMLElement
  hasSearched?: boolean
}>()

const store = useStore()
const { mobile } = useDisplay()

const query = computed(() => props.query?.trim() ?? '')
const searchResults = computed(() => store.state.documents.searchFilesResults || [])
const searchResultsTotalCount = computed(() => store.state.documents.searchFilesResultsPagination?.itemCount || 0)
const isPending = computed(() => store.state.documents.searchFilesPending)
const searchPageName = computed(() =>
  (store.getters['room/roomMnemo']
    ? PROJECT_SEARCH_ROUTE_NAME
    : GLOBAL_SEARCH_ROUTE_NAME
  ),
)
const searchPageParams = computed(() => {
  if (searchPageName.value === PROJECT_SEARCH_ROUTE_NAME) {
    return {
      roomType: getRoomRouteTypeFromType(store.state.room.currentRoom.type),
      mnemo: store.getters['room/roomMnemo'],
    }
  }
  return undefined
})
const menuOpen = ref(false)
watch([query, () => props.hasSearched], ([val, searched]) => {
  menuOpen.value = searched || !!val
})

defineExpose({
  openMenu: () => {
    menuOpen.value = true
  },
  closeMenu: () => {
    menuOpen.value = false
  },
})
</script>

<style lang="scss">
$searchInputMaxWidth: 560px;
$searchInputMenuMargins: 24px;

.AppBarDocumentSearch__results {
  top: 64px !important;
  background: white;
}

// If we can fit more than the design system spec maximum width (max-width below) + margins, we restrict it
@media screen and (min-width: calc($searchInputMaxWidth + $searchInputMenuMargins)) {
  .AppBarDocumentSearch__results {
    // Override the default max-width applied inline by v-menu
    // It matches the maximum width of the AppBar search input (design system spec)
    max-width: $searchInputMaxWidth !important;
  }
}

@media screen and (max-width: calc($searchInputMaxWidth + $searchInputMenuMargins)) {
  .AppBarDocumentSearch__results__container {
    // Force the container to expand to the screen width, minus the margins
    width: calc(100vw - $searchInputMenuMargins);
  }
}

.AppBarDocumentSearch__scrollable-list {
  max-height: 466px;
  overflow-y: auto;
}
</style>
